import { Avatar, CircularProgress, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useGetTeamMembersQuery } from '../../api/teams'
import { routesProfile } from '../../consts'
import modalEmitter from '../ModalComponent/modalEmitter'
import { ProjectTeamRoleChip } from '../ProjectPage/ProjectTeamRoleChip'
import { StyledBadge } from '../ui-kit'
import {
  canNavigateToUserProfile,
  getUserInitialsFromDisplayName,
  resolveUserAvatarUrl,
  resolveUserDisplayName,
} from '../../utils/deletedUserDisplay'

type TeamMembersModalBodyProps = {
  teamId: number
}

export const TeamMembersModalBody = ({ teamId }: TeamMembersModalBodyProps) => {
  const navigate = useNavigate()
  const { data: members = [], isLoading, isError } = useGetTeamMembersQuery({ teamId })

  const handleOpenProfile = (slug: string) => {
    const pathKey = slug.trim()
    if (!pathKey) {
      return
    }
    modalEmitter.emitCloseModal()
    navigate(`/${routesProfile.USERS.path}/${pathKey}`)
  }

  if (isLoading) {
    return (
      <Stack direction="row" gap={2} alignItems="center">
        <CircularProgress size={20} />
        <Typography>Загружаем участников…</Typography>
      </Stack>
    )
  }

  if (isError) {
    return <Typography color="error">Не удалось загрузить участников команды.</Typography>
  }

  if (members.length === 0) {
    return <Typography>В команде пока нет участников.</Typography>
  }

  return (
    <Stack gap={2}>
      {members.map((member) => {
        const slug = (member.user.slug ?? '').trim()
        const title = resolveUserDisplayName(member.user)
        const hasSlug = canNavigateToUserProfile(member.user)
        return (
          <Stack
            key={`${member.user.id ?? 'deleted'}-${member.team_role}-${member.is_author ? 'author' : 'member'}`}
            direction="row"
            gap={1.5}
            alignItems="center"
            sx={{ cursor: hasSlug ? 'pointer' : 'default' }}
            onClick={hasSlug ? () => handleOpenProfile(slug) : undefined}
          >
            <StyledBadge
              size={10}
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
              invisible={!member.user.is_online}
            >
              <Avatar src={resolveUserAvatarUrl(member.user)} alt={title}>
                {getUserInitialsFromDisplayName(title)}
              </Avatar>
            </StyledBadge>
            <Stack gap={0.25}>
              <ProjectTeamRoleChip
                teamRole={member.is_author ? 'author' : member.team_role}
                roleLabel={member.role_label}
                isAuthor={member.is_author}
                entity="team"
              />
              <Typography>{title}</Typography>
            </Stack>
          </Stack>
        )
      })}
    </Stack>
  )
}
